import type { Changelog, ChangelogPr, ReleaseStatus } from '@uchat-team-management/shared';
import { extractWorkItemRefs } from '@uchat-team-management/shared';

/** Default for the editable Message line, per status. */
const DEFAULT_MESSAGE: Record<ReleaseStatus, string> = {
  draft: 'Draft',
  running: 'กำลัง build',
  succeeded: 'Build เสร็จแล้ว',
  failed: 'Build ไม่ผ่าน',
  publishing: 'กำลัง deploy',
  published: 'Published แล้ว',
};

export const defaultMessage = (status: ReleaseStatus): string => DEFAULT_MESSAGE[status];

/**
 * รอบที่ N = position of this build among all builds of the same
 * product+environment+version, ordered by build number.
 */
export const versionRound = (
  groups: readonly { version: string; builds: readonly { buildNumber: number }[] }[],
  version: string,
  buildNumber: number,
): number => {
  const group = groups.find((g) => g.version === version);
  if (!group) return 1;
  return Math.max(1, group.builds.filter((b) => b.buildNumber <= buildNumber).length);
};

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const link = (href: string, label: string): string =>
  href ? `<a href="${esc(href)}">${esc(label)}</a>` : esc(label);

/** …/pull/3391 → …/commit/<sha>; '' when the PR url doesn't look like GitHub. */
const commitUrlFromPrUrl = (prUrl: string, sha: string): string =>
  /\/pull\/\d+$/.test(prUrl) ? prUrl.replace(/\/pull\/\d+$/, `/commit/${sha}`) : '';

/** Refs frozen per PR, falling back to title extraction for legacy snapshots. */
const prRefs = (pr: ChangelogPr): string[] => {
  if (pr.refs) return pr.refs;
  const extracted = extractWorkItemRefs([pr.title]);
  return [...extracted.clickup, ...extracted.linear];
};

export interface Line {
  text: string;
  html: string;
  /** GitHub login of the PR/commit author — drives "Select me" (UCH-514). */
  authorLogin: string | null;
}

export interface StatusItems {
  /** Bullets with a resolvable card ref. */
  readyItems: Line[];
  /** Refless bullets (no ticket / not found). */
  manualItems: Line[];
}

/** Horizontal rule framing the Message line (UCH-514). */
export const SUMMARY_SEPARATOR = '--------------';

export interface BuildStatusInput {
  productName: string;
  environment: string;
  version: string;
  buildNumber: number;
  status: ReleaseStatus;
  round: number;
  /** The editable Message line; see defaultMessage(status). */
  message: string;
  changelog: Pick<Changelog, 'prs' | 'commits' | 'tasks' | 'taskDetails'>;
}

export interface StatusMessage {
  /** Plain-text fallback: card number followed by its URL in parentheses. */
  text: string;
  /** Rich variant: card number / #PR / short sha are hyperlinks. */
  html: string;
  /** Header block alone (no message/details) — for the preview dialog. */
  headerText: string;
  /** Ready-for-QA + Manual-review sections alone; '' when empty. */
  detailsText: string;
}

/**
 * Card-first bullets (UCH-423): the card number leads, card title is the main
 * text (PR title as fallback), no card status; refless entries carry the PR
 * head commit instead of a branch. Split from composition so the preview
 * dialog can offer per-item selection (UCH-514).
 */
export const buildStatusItems = (
  changelog: Pick<Changelog, 'prs' | 'commits' | 'tasks' | 'taskDetails'>,
): StatusItems => {
  const cardTitle = (ref: string): { title: string; url: string } => {
    const detail = changelog.taskDetails?.[ref];
    const frozen = changelog.tasks.find((t) => t.ref === ref);
    return {
      title: detail?.title || frozen?.title || '',
      url: detail?.url || frozen?.url || '',
    };
  };

  const readyLines: Line[] = [];
  const manualLines: Line[] = [];

  const pushItem = (item: {
    refs: string[];
    fallbackTitle: string;
    authorLogin: string | null | undefined;
    /** e.g. `in #3408` / `in a1b2c3d` */
    whereText: string;
    whereHtml: string;
    /** short head sha + its url, manual bullets only */
    sha?: { short: string; url: string };
  }) => {
    const by = item.authorLogin ? `by @${item.authorLogin} ` : '';
    if (item.refs.length === 0) {
      const shaText = item.sha ? ` (${item.sha.short})` : '';
      const shaHtml = item.sha ? ` (${link(item.sha.url, item.sha.short)})` : '';
      manualLines.push({
        text: `• ${item.fallbackTitle} ${by}${item.whereText}${shaText}`,
        html: `• ${esc(item.fallbackTitle)} ${esc(by)}${item.whereHtml}${shaHtml}`,
        authorLogin: item.authorLogin ?? null,
      });
      return;
    }
    // Every ref links to its own card; the first resolvable title leads the text.
    const cards = item.refs.map((ref) => ({ ref, ...cardTitle(ref) }));
    const title = cards.find((c) => c.title)?.title || item.fallbackTitle;
    const refsText = cards.map((c) => `${c.ref}${c.url ? ` (${c.url})` : ''}`).join(', ');
    const refsHtml = cards.map((c) => link(c.url, c.ref)).join(', ');
    readyLines.push({
      text: `• ${refsText} ${title} — ${by}${item.whereText}`,
      html: `• ${refsHtml} ${esc(title)} — ${esc(by)}${item.whereHtml}`,
      authorLogin: item.authorLogin ?? null,
    });
  };

  if (changelog.prs.length > 0) {
    for (const pr of [...changelog.prs].sort((a, b) => a.number - b.number)) {
      pushItem({
        refs: prRefs(pr),
        fallbackTitle: pr.title,
        authorLogin: pr.authorLogin,
        whereText: `in #${pr.number}`,
        whereHtml: `in ${link(pr.url, `#${pr.number}`)}`,
        sha: pr.headSha
          ? { short: pr.headSha.slice(0, 7), url: commitUrlFromPrUrl(pr.url, pr.headSha) }
          : undefined,
      });
    }
  } else {
    for (const commit of changelog.commits) {
      const extracted = extractWorkItemRefs([commit.subject]);
      pushItem({
        refs: [...extracted.clickup, ...extracted.linear],
        fallbackTitle: commit.subject,
        authorLogin: commit.authorLogin,
        whereText: `in ${commit.sha.slice(0, 7)}`,
        whereHtml: `in ${link(commit.url, commit.sha.slice(0, 7))}`,
      });
    }
  }

  return { readyItems: readyLines, manualItems: manualLines };
};

/**
 * Assemble the chat-ready message (UCH-514 format): separators frame the
 * Message line; each section heading is bold with a blank line above it and a
 * tight bullet list under it. A section whose items were all deselected
 * disappears, heading included, and with no sections at all the second
 * separator is dropped.
 */
export const composeStatusMessage = (
  input: Omit<BuildStatusInput, 'changelog'>,
  items: StatusItems,
): StatusMessage => {
  // Values in backticks: chats that render markdown show them as code chips;
  // the rich variant uses <code> instead.
  const headerText = [
    input.productName,
    `Env: \`${input.environment.toUpperCase()}\``,
    `Version: \`${input.version}\` build \`${input.buildNumber}\``,
    `Round: \`${input.round}\``,
  ].join('\n');
  const code = (v: string | number) => `<code>${esc(String(v))}</code>`;
  const headerHtml = [
    esc(input.productName),
    `Env: ${code(input.environment.toUpperCase())}`,
    `Version: ${code(input.version)} build ${code(input.buildNumber)}`,
    `Round: ${code(input.round)}`,
  ].join('<br>');

  // Headings bold (markdown ** / <b>) with a blank line above; bullets are a
  // plain tight list under their heading.
  const section = (heading: string, lines: Line[]) => ({
    text: `**${heading}**\n${lines.map((l) => l.text).join('\n')}`,
    html: `<b>${heading}</b><br>${lines.map((l) => l.html).join('<br>')}`,
  });
  const detailSections: { text: string; html: string }[] = [];
  if (items.readyItems.length > 0) detailSections.push(section('Ready for QA:', items.readyItems));
  if (items.manualItems.length > 0) {
    detailSections.push(section('Manual review (no ticket / not found):', items.manualItems));
  }
  const detailsText = detailSections.map((s) => s.text).join('\n\n');
  const detailsHtml = detailSections.map((s) => s.html).join('<br><br>');
  const text = [
    [headerText, SUMMARY_SEPARATOR, input.message].join('\n'),
    ...(detailsText ? [`${SUMMARY_SEPARATOR}\n\n${detailsText}`] : []),
  ].join('\n');
  const html = [
    [headerHtml, SUMMARY_SEPARATOR, esc(input.message)].join('<br>'),
    ...(detailsHtml ? [`${SUMMARY_SEPARATOR}<br><br>${detailsHtml}`] : []),
  ].join('<br>');
  return { text, html, headerText, detailsText };
};

/** Full summary — every item included. */
export const buildStatusMessage = (input: BuildStatusInput): StatusMessage =>
  composeStatusMessage(input, buildStatusItems(input.changelog));
